import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  ArrowUpRight, 
  ArrowDownLeft,
  Filter,
  Download,
  Wallet,
  PiggyBank,
  Sparkles
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";
import EmptyState from "../components/EmptyState";

const currencySymbols = {
  JMD: "J$",
  USD: "$",
  GBP: "£",
  CAD: "C$"
};

// Helper to identify round-up transactions
const isRoundUpTransaction = (tx) => {
  return tx.description?.toLowerCase().includes("round-up") || 
         tx.reference_id?.startsWith("roundup_");
};

// Helper to identify SmartPot credit (savings received)
const isSmartPotCredit = (tx) => {
  return tx.description === "AI Round-Up Savings" && tx.type === "credit";
};

// Helper to identify main wallet round-up debit
const isMainWalletRoundUpDebit = (tx) => {
  return tx.description === "AI Round-Up → SmartPot" && tx.type === "debit";
};

const Transactions = ({ user }) => {
  const navigate = useNavigate();

  const { data: transactions, isLoading } = useQuery({
    queryKey: ["allTransactions"],
    queryFn: async () => {
      const response = await axios.get(`${API}/transactions?limit=100`);
      return response.data;
    }
  });

  // Filter out main wallet round-up debits (they're just internal movements)
  // Keep SmartPot credits as they show savings growth
  const filteredTransactions = transactions?.filter(tx => {
    // Hide the main wallet debit for round-ups (internal movement)
    if (isMainWalletRoundUpDebit(tx)) {
      return false;
    }
    return true;
  }) || [];

  // Group transactions by date
  const groupedTransactions = filteredTransactions.reduce((groups, tx) => {
    const date = new Date(tx.created_at).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(tx);
    return groups;
  }, {});

  // Get transaction display info
  const getTransactionDisplay = (tx) => {
    // SmartPot round-up savings (credit to SmartPot)
    if (isSmartPotCredit(tx)) {
      return {
        icon: <PiggyBank className="w-5 h-5" />,
        iconBg: "bg-pink-500/10 text-pink-400",
        label: "AI Round-Up Savings",
        sublabel: "Saved to SmartPot",
        amountColor: "text-pink-400",
        prefix: "+",
        showSparkle: true
      };
    }
    
    // Regular credit (money received)
    if (tx.type === "credit") {
      return {
        icon: <ArrowDownLeft className="w-5 h-5" />,
        iconBg: "bg-green-500/10 text-green-400",
        label: tx.description || "Money Received",
        sublabel: null,
        amountColor: "text-green-400",
        prefix: "+",
        showSparkle: false
      };
    }
    
    // Regular debit (money sent)
    return {
      icon: <ArrowUpRight className="w-5 h-5" />,
      iconBg: "bg-red-500/10 text-red-400",
      label: tx.description || "Money Sent",
      sublabel: null,
      amountColor: "text-red-400",
      prefix: "-",
      showSparkle: false
    };
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="transactions-page">
      <header className="px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-semibold text-xl">Transactions</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" className="text-muted-foreground">
            <Filter className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" className="text-muted-foreground">
            <Download className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="px-6">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-20 rounded-2xl bg-secondary animate-pulse" />
            ))}
          </div>
        ) : filteredTransactions.length === 0 ? (
          <EmptyState
            icon={Wallet}
            headline="Your activity starts here"
            description="When you send, receive, or exchange money, it will appear in your history."
            actionLabel="Add Funds"
            onAction={() => navigate("/dashboard")}
            secondaryLabel="Learn how Stelx Pay works"
            onSecondary={() => {}}
          />
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedTransactions).map(([date, txs]) => (
              <div key={date} className="animate-slide-up">
                <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-3">
                  {date}
                </p>
                <div className="glass rounded-2xl divide-y divide-white/5">
                  {txs.map((tx, index) => {
                    const display = getTransactionDisplay(tx);
                    
                    return (
                      <div
                        key={tx.transaction_id || index}
                        className="p-4 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${display.iconBg}`}>
                            {display.icon}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-foreground line-clamp-1">
                                {display.label}
                              </p>
                              {display.showSparkle && (
                                <Sparkles className="w-3.5 h-3.5 text-pink-400" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {display.sublabel || new Date(tx.created_at).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`font-mono-numbers text-sm font-medium ${display.amountColor}`}>
                            {display.prefix}
                            {currencySymbols[tx.currency] || ""}
                            {tx.amount.toLocaleString(undefined, { 
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2 
                            })}
                          </span>
                          <p className="text-xs text-muted-foreground capitalize">
                            {tx.status}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <NavBar active="wallet" />
    </div>
  );
};

export default Transactions;
